import { FC, Fragment, useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { FormInput, ModalWrapper, TextField } from '@microservices/wiskey-react-components'
import { Autocomplete } from '@mui/material'
import ClearIcon from '@mui/icons-material/Clear'
import DeleteIcon from '@mui/icons-material/Delete'
import { Box, Button, Divider, IconButton, SxProps, Typography } from '@mui/material'

import { FieldPickerController } from '@components/hookFormControllers/FieldPickerController/FieldPickerController'

import { useFetchAllObjectQuery } from '@redux/api'

import { FIELD_VALUE_TYPE, GENERATOR_INPUT_TYPE, MIN_LABEL_WIDTH } from '@constants'
import { AutocompleteOption, EntityCommandDTO } from '@types'

import { OptionsFilter } from '@gantt/types'

export type EmbeddedObject = {
  objectCode: string | null
  field: string | null
  refValueType?: FIELD_VALUE_TYPE
  valueType?: FIELD_VALUE_TYPE
  isPk?: boolean
  isInternalId?: boolean
  model?: string | null
  description?: string | null
}

export type RefPathValue = {
  path: string
  pathArray: EmbeddedObject[]
  field?: string
  commands?: EntityCommandDTO[]
}

export type ModalRefPathPickerProps = {
  value?: RefPathValue
  onChange?: (value?: RefPathValue) => void
  onSave?: (value: RefPathValue) => void
  onClear?: () => void
  isDisabled?: boolean
  isRequired?: boolean
  isEdit?: boolean
  isFlatOptions?: boolean
  hasField?: boolean
  hasClearInInput?: boolean
  label?: string
  labelPlacement?: 'bottom' | 'top' | 'end' | 'start'
  placeholder?: string
  refPickAllowed?: boolean
  existingPaths?: string[]
  selectedPaths?: string[]
  optionsFilter?: OptionsFilter
  object?: AutocompleteOption<string> | null
  objectOptions?: AutocompleteOption[]
  refObjectOptions?: AutocompleteOption[]
  onChangeRootObject?: (option: AutocompleteOption<string>) => void
  sx?: SxProps
  sxPickButton?: SxProps
  formInputContainerSx?: SxProps
}

type ModalFormValues = {
  pathArray: EmbeddedObject[]
}

const buildPathFromArray = (embeddedObjects: EmbeddedObject[]) =>
  embeddedObjects
    .map(obj => obj.field)
    .filter(Boolean)
    .join('.')

export const ModalRefPathPicker: FC<ModalRefPathPickerProps> = ({
  value,
  onChange,
  onSave,
  onClear,
  isDisabled = false,
  isRequired = false,
  isEdit = false,
  isFlatOptions = false,
  hasField = false,
  hasClearInInput = false,
  label,
  labelPlacement,
  placeholder,
  refPickAllowed = false,
  existingPaths,
  selectedPaths,
  optionsFilter,
  object,
  objectOptions: objectOptionsProp,
  refObjectOptions,
  onChangeRootObject,
  sx,
  sxPickButton,
  formInputContainerSx,
}) => {
  const { t } = useTranslation()
  const [show, setShow] = useState(false)

  const methods = useForm<ModalFormValues>({
    defaultValues: {
      pathArray: value?.pathArray || [],
    },
  })

  const {
    watch,
    setValue,
    reset,
    formState: { isDirty },
  } = methods

  const watchedPathArray = watch('pathArray')

  const {
    data: fetchedObjects,
    isLoading: isLoadingObjects,
    isFetching: isFetchingObjects,
  } = useFetchAllObjectQuery(undefined, {
    skip: !show || !!objectOptionsProp,
  })

  const objectOptions: AutocompleteOption[] = useMemo(() => {
    if (objectOptionsProp?.length) return objectOptionsProp
    if (refObjectOptions?.length) return refObjectOptions
    return fetchedObjects?.map(obj => ({ id: obj.code, label: obj.title })) || []
  }, [objectOptionsProp, refObjectOptions, fetchedObjects])

  const loadingObjects = isLoadingObjects || isFetchingObjects

  const handleClear = () => {
    onChange?.(undefined)
    onClear?.()
    setValue('pathArray', [])
  }

  const handleSave = () => {
    const cleanedPathArray = (watchedPathArray || []).filter(item => item?.field) as EmbeddedObject[]
    const path = buildPathFromArray(cleanedPathArray)
    const nextValue: RefPathValue = {
      path,
      pathArray: cleanedPathArray,
      field: hasField ? path.replace(/\./g, '') : undefined,
    }

    onChange?.(nextValue)
    onSave?.(nextValue)
    setShow(false)
  }

  const handleOpen = () => {
    reset({
      pathArray: value?.pathArray || [],
    })
    setShow(true)
  }

  const handleClose = () => {
    reset({
      pathArray: value?.pathArray || [],
    })
    setShow(false)
  }

  const handleChangeDefaultObject = (option: AutocompleteOption<string>) => {
    setValue('pathArray', [
      {
        objectCode: option?.id || null,
        refValueType: FIELD_VALUE_TYPE.OBJ_PK_LINK,
        field: null,
      },
    ])
    onChangeRootObject?.(option)
    onChange?.({
      path: '',
      pathArray: [
        {
          objectCode: option?.id || null,
          refValueType: FIELD_VALUE_TYPE.OBJ_PK_LINK,
          field: null,
        },
      ],
    })
  }

  const pathDisplay = value?.path || ''

  return (
    <Fragment>
      <Box alignItems='center' display='flex' gap='0 5px' justifyContent='space-between' sx={sx}>
        <Box flex={1}>
          <FormInput
            formInputContainerSx={formInputContainerSx}
            inputType={GENERATOR_INPUT_TYPE.INPUT}
            label={label}
            labelPlacement={labelPlacement}
            name='refPathDisplay'
            placeholder={placeholder ?? t('placeholder.refPath')}
            readOnly
            rules={{
              required: isRequired,
            }}
            value={pathDisplay}
            textFieldInputProps={{
              ...(hasClearInInput && {
                endAdornment: (
                  <IconButton
                    disabled={!pathDisplay}
                    size='small'
                    sx={{ width: 36.5, py: '10px' }}
                    onClick={handleClear}
                  >
                    <ClearIcon fontSize='small' />
                  </IconButton>
                ),
              }),
            }}
          />
        </Box>
        <Box display='flex' mb={1}>
          <Button
            disabled={isDisabled}
            variant='contained'
            sx={{
              margin: '0 15px',
              ...(sxPickButton ? sxPickButton : {}),
            }}
            onClick={handleOpen}
          >
            {pathDisplay ? t('ganttCreate.refEdit') : t('ganttCreate.refPick')}
          </Button>
          {!hasClearInInput && (
            <IconButton size='small' sx={{ width: 35 }} onClick={handleClear}>
              <DeleteIcon fontSize='small' />
            </IconButton>
          )}
        </Box>
      </Box>

      {show && (
        <FormProvider {...methods}>
          <ModalWrapper
            btnText={t('ganttCreate.common.apply')}
            disabledSave={!isDirty && !!pathDisplay}
            isShow={show}
            onClose={handleClose}
            onSave={handleSave}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, mb: 2 }}>
              <Typography
                sx={{
                  width: MIN_LABEL_WIDTH,
                  minWidth: MIN_LABEL_WIDTH,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {t('label.defaultObject')}
              </Typography>
              <Autocomplete
                disableClearable
                disabled={!onChangeRootObject}
                loading={loadingObjects}
                options={objectOptions}
                size='small'
                sx={{ width: '100%' }}
                value={object || undefined}
                getOptionLabel={option => option.label || ''}
                renderInput={params => (
                  <TextField
                    {...params}
                    inputProps={{ ...params.inputProps }}
                    placeholder={t('placeholder.defaultObject')}
                    variant='outlined'
                  />
                )}
                onChange={(_, option) => {
                  if (option) {
                    handleChangeDefaultObject(option as AutocompleteOption<string>)
                  }
                }}
              />
            </Box>

            <Box display='flex' mt={1}>
              <Box width={120}>{t('ganttCreate.modalPathPickerObjectsColumn')}</Box>
              <Divider flexItem orientation='vertical' />
              <Box pl={1}>{t('ganttCreate.modalPathPickerFieldsColumn')}</Box>
            </Box>

            <Box py='15px'>
              <FieldPickerController
                existingPaths={existingPaths}
                isEdit={isEdit}
                isFlatOptions={isFlatOptions}
                name='pathArray'
                object={object}
                optionsFilter={optionsFilter}
                refPickAllowed={refPickAllowed}
                selectedPaths={selectedPaths}
              />
            </Box>
          </ModalWrapper>
        </FormProvider>
      )}
    </Fragment>
  )
}

